import React, { useState, useEffect } from 'react';
import { Star, Info, BookmarkPlus, Check, Edit2, Save } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useFetcher } from 'react-router';

interface ScriptImproverProps {
  originalScript: string;
  improvedScript?: string | null;
  intent: string;
  analysisId?: string;
  title?: string;
  readOnly?: boolean;
}

export function ScriptImprover({ originalScript, improvedScript, intent, analysisId, title, readOnly = false }: ScriptImproverProps) {
  const { _ } = useLingui();
  const [isSaved, setIsSaved] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedScript, setEditedScript] = useState(improvedScript || '');
  const fetcher = useFetcher<{ success?: boolean; error?: string }>();

  // Handle fetcher state changes
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (fetcher.data.success) {
        toast.success(_(msg`Script updated successfully!`));
        setIsEditing(false);
        
        // Update localStorage if this script was already saved for practice
        if (analysisId) {
          const savedScripts = JSON.parse(localStorage.getItem('practiceScripts') || '[]');
          const scriptIndex = savedScripts.findIndex((s: any) => s.analysisId === analysisId);
          if (scriptIndex !== -1) {
            savedScripts[scriptIndex].script = editedScript;
            localStorage.setItem('practiceScripts', JSON.stringify(savedScripts));
          }
        }
      } else if (fetcher.data.error) {
        toast.error(fetcher.data.error || _(msg`Failed to save script. Please try again.`));
      }
    }
  }, [fetcher.state, fetcher.data, analysisId, editedScript]);

  // Don't display anything if there's no improved script
  if (!improvedScript) {
    return null;
  }

  const handleEditSave = () => {
    if (isEditing) {
      // Save mode - update the script
      if (!analysisId || !editedScript) {
        toast.error(_(msg`Missing analysis ID or script content`));
        return;
      }

      fetcher.submit(
        {
          analysisId,
          improvedScript: editedScript,
        },
        {
          method: 'post',
          action: '/api/update-script',
        }
      );
    } else {
      // Edit mode - switch to editing
      setIsEditing(true);
    }
  };

  const handleSaveForPractice = () => {
    if (!analysisId) return;
    
    const scriptToSave = isEditing ? editedScript : improvedScript;
    if (!scriptToSave) return;

    // Get existing saved scripts from localStorage
    const savedScripts = JSON.parse(localStorage.getItem('practiceScripts') || '[]');
    
    // Check if already saved
    const alreadySaved = savedScripts.some((s: any) => s.analysisId === analysisId);
    if (alreadySaved) {
      toast.info(_(msg`This script is already saved for practice`));
      setIsSaved(true);
      return;
    }

    // Add new script
    const scriptData = {
      analysisId,
      script: scriptToSave,
      intent,
      savedAt: Date.now(),
      title: title + `${new Date().toLocaleDateString()}`  || `${intent.charAt(0).toUpperCase() + intent.slice(1)} Script - ${new Date().toLocaleDateString()}`,
    };

    savedScripts.push(scriptData);
    localStorage.setItem('practiceScripts', JSON.stringify(savedScripts));
    
    setIsSaved(true);
    toast.success(_(msg`Script saved! You can practice with it in your next session.`));
  };

  return (
    <div className="border-t pt-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-bold text-brand-dark-blue flex items-center gap-2">
            <Star className="h-5 w-5 text-brand-gold" />
            <Trans>AI-Improved Script</Trans>
          </h4>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleEditSave}
            disabled={fetcher.state === 'submitting' || readOnly}
            className="text-brand-dark-blue border-brand-dark-blue/20 hover:bg-brand-dark-blue/5"
          >
            {fetcher.state === 'submitting' ? (
              <>
                <Save className="h-4 w-4 mr-2 animate-pulse" />
                <Trans>Saving...</Trans>
              </>
            ) : isEditing ? (
              <>
                <Save className="h-4 w-4 mr-2" />
                <Trans>Save</Trans>
              </>
            ) : (
              <>
                <Edit2 className="h-4 w-4 mr-2" />
                <Trans>Edit</Trans>
              </>
            )}
          </Button>
        </div>
        
        <div className={`${isEditing ? 'bg-white' : 'bg-gradient-to-br from-brand-gold/10 to-brand-green/5'} border-2 border-brand-gold/20 rounded-lg p-6`}>
          {isEditing ? (
            <textarea
              value={editedScript}
              onChange={(e) => setEditedScript(e.target.value)}
              className="w-full min-h-[200px] whitespace-pre-wrap text-sm leading-6 text-brand-dark-blue font-medium bg-white border-none outline-none resize-y"
              placeholder="Edit your improved script here..."
            />
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-6 text-brand-dark-blue font-medium">
              {editedScript || improvedScript}
            </div>
          )}
        </div>
        
        <div className="text-sm text-brand-dark-blue/60 bg-brand-gold/5 p-4 rounded border border-brand-gold/20">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-brand-gold" />
            <span>
              <Trans>
                This improved script is based on your original content and optimized for your {intent === 'other' ? 'practice session' : intent} intent.
                Use it as inspiration to enhance your delivery.
              </Trans>
            </span>
          </div>
          <div className="flex pt-4">
          <Button
            onClick={handleSaveForPractice}
            disabled={isSaved || readOnly}
            className={`${
              isSaved || readOnly
                ? 'bg-brand-green/20 text-brand-green hover:bg-brand-green/20 cursor-default'
                : 'bg-gradient-to-r from-brand-gold to-brand-green hover:from-brand-gold/90 hover:to-brand-green/40 text-white'
            } shadow-lg transition-all duration-300`}
          >
            {isSaved ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                <Trans>Saved for Practice</Trans>
              </>
            ) : (
              <>
                <BookmarkPlus className="h-4 w-4 mr-2" />
                <Trans>Save for Next Practice</Trans>
              </>
            )}
          </Button>
        </div>
        </div>

       
      </div>
    </div>
  );
}
